import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import IconArrowRight from '@apollo/icons/small/IconArrowRight.svg';
import { IconButton, ListItem } from '@apollo/orbit';
import classnames from 'classnames';
import {
  GraphQLField,
  GraphQLInterfaceType,
  GraphQLObjectType,
  GraphQLUnionType,
} from 'graphql';
import React, { useState } from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLNode, unwrapNestedType } from 'src/lib/graphql/graphqlHelpers';
import { GetIsEntity } from 'src/lib/graphql/subgraphMetadataHelpers';

import { useAnalyticsContext } from '../hooks/useAnalyticsContext';

import { EntityIcon } from './EntityIcon';
import { FieldToggle } from './fieldToggle/FieldToggle';

export const Field = ({
  field,
  onSelect,
  toggleNodeInOperation,
  isNodeInOperation,
  insertAllFieldsRecursively,
  getIsEntity,
}: {
  field: GraphQLField<unknown, unknown>;
  onSelect?: (field: GraphQLNode) => void;
  toggleNodeInOperation?: (node: GraphQLField<unknown, unknown>) => void;
  isNodeInOperation?: (node: GraphQLField<unknown, unknown>) => boolean;
  insertAllFieldsRecursively?: (
    node: GraphQLField<unknown, unknown>,
    includeNode: boolean,
  ) => void;
  getIsEntity: GetIsEntity | undefined;
}) => {
  const { track } = useAnalyticsContext();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const fieldIsToggled = isNodeInOperation?.(field) || false;
  const fieldReturnTypeName = field.type.toString();
  const fieldDescription = field.description ?? '';
  const fieldShouldExpand = field.args.length > 0;

  return (
    <ListItem
      className={classnames(
        // pl-10 accounts for checkbox button that's absolutely positioned
        toggleNodeInOperation && isNodeInOperation && 'pl-10',
        'group relative flex-1 gap-2 font-mono text-sm',
      )}
      onClick={() => onSelect?.(field)}
      {...(fieldShouldExpand && {
        onFocus: () => setIsExpanded(true),
        onBlur: () => setIsExpanded(false),
      })}
    >
      {toggleNodeInOperation && isNodeInOperation && (
        <Tooltip
          label={fieldIsToggled ? 'Remove from query' : 'Add to query'}
          title={fieldDescription}
        >
          <IconButton
            // these button overrides are because our "hidden" variant doesn't work well
            // in Explorer where its bg is the same as Explorer's bg
            className="absolute left-1 size-7 min-w-0 bg-transparent hover:bg-btn-secondary"
            aria-label="Toggle field to be checked or unchecked"
            size="sm"
            variant="hidden"
            onClick={(event) => {
              event.stopPropagation();

              if (fieldIsToggled) {
                track(`Toggle Field Off`);
              } else {
                track(`Toggle Field On`);
              }

              // if a field that has subfields, open that type in the docs
              const type = unwrapNestedType(field.type);
              if (!fieldIsToggled && (event.metaKey || event.ctrlKey)) {
                insertAllFieldsRecursively?.(field, true);
              } else {
                if (
                  !isNodeInOperation(field) &&
                  (type instanceof GraphQLObjectType ||
                    type instanceof GraphQLInterfaceType ||
                    type instanceof GraphQLUnionType)
                ) {
                  onSelect?.(field);
                }
                toggleNodeInOperation(field);
              }
            }}
            icon={
              <FieldToggle checked={fieldIsToggled ? 'checked' : 'unchecked'} />
            }
          />
        </Tooltip>
      )}
      <span
        className={classnames(
          'items-center truncate text-primary',
          field.isDeprecated && 'line-through',
        )}
      >
        {field.name}
        {fieldShouldExpand && (
          <span
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
          >
            (…)
          </span>
        )}
        :
      </span>
      <span className="h-auto flex-1 truncate text-code-f">
        {fieldReturnTypeName}
      </span>
      {field.isDeprecated && (
        <Tooltip
          placement="top"
          label={
            field.deprecationReason
              ? `Deprecated: ${field.deprecationReason}`
              : 'Deprecated'
          }
        >
          <IconWarningSolid className="flex-none cursor-pointer" />
        </Tooltip>
      )}
      {getIsEntity?.(unwrapNestedType(field.type)) && (
        <EntityIcon
          size="small"
          className="hidden flex-none group-hover:flex group-focus-visible:flex"
        />
      )}
      <IconArrowRight className="hidden flex-none group-hover:block group-focus-visible:block" />
      {fieldShouldExpand && (
        <div
          className={classnames(
            'pointer-events-none absolute left-0 top-0 flex h-auto w-full items-stretch',
            // Note: Setting this as invisible makes it so the scrollbar accounts
            // for hover items at the bottom. without this, hover items may extend
            // past the viewport and it becomes hard for the user to scroll since
            // they may lose their hover state over the ellipsis.
            isExpanded ? 'visible' : 'invisible',
          )}
        >
          <div className="z-10 ml-8 flex flex-1 items-start gap-2 truncate whitespace-pre rounded bg-btn-secondary-hover py-2 pl-2 pr-4 font-mono text-sm">
            <span
              className={classnames(
                'flex-1 text-primary',
                field.isDeprecated && 'line-through',
              )}
            >
              {field.name}(
              {field.args.map((arg) => (
                <div key={arg.name} className="ml-2">
                  <span> {arg.name}:</span>
                  <span className="ml-1 text-code-f">
                    {arg.type.toString()}
                  </span>
                </div>
              ))}
              ): <span className="text-code-f">{fieldReturnTypeName}</span>
            </span>
            {field.isDeprecated && <IconWarningSolid />}
            {getIsEntity?.(unwrapNestedType(field.type)) && (
              <EntityIcon size="small" className="mt-1 flex-none" />
            )}
            <IconArrowRight className="mt-1.5 flex-none" />
          </div>
        </div>
      )}
    </ListItem>
  );
};
